import { json } from '@codemirror/lang-json';
import { CircleStackIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@langchain/untitled-ui-icons';
import { Link, Modal, ModalClose, ModalDialog } from '@mui/joy';

import { useEffect, useMemo, useState } from 'react';

import { Code, CodeCard } from '@/components/Code/Code';
import { TransformedExampleWarning } from '@/components/ExampleCrudPane/TransformedExampleWarning';
import { Button } from '@/design-system';
import { useRun, useValidateExample, useWorkspaceId } from '@/hooks/useSwr';
import { DatasetSchema, RunSchema } from '@/types/schema';
import { pluralize } from '@/utils/stringUtils';
import { cn } from '@/utils/tailwind';

import { appDatasetsPath, appOrganizationPath } from '../../../utils/constants';
import { Popover, PopoverContent, PopoverTrigger } from '../../Popover';
import { BulkActionResult } from '../types';
import { handleAddToDataset } from '../utils/handleAddToDataset';
import { DatasetSelectorPopoverContent } from './DatasetSelectorPopoverContent';

function InputsOutputsSection(props: {
  inputs?: Record<string, any>;
  outputs?: Record<string, any>;
}) {
  const inputsStr = JSON.stringify(props.inputs ?? {}, null, 2);
  const outputsStr = JSON.stringify(props.outputs ?? {}, null, 2);

  const [showInputs, setShowInputs] = useState(true);
  const [showOutputs, setShowOutputs] = useState(true);

  return (
    <div className="flex min-w-[650px] flex-col gap-2 pt-2">
      <TransformedExampleWarning
        message={`This dataset contains transformations that could be applied to matching 
          selected runs. Below is a preview of the first example's inputs and outputs that will be added.`}
      />

      <div className="flex flex-col gap-2 rounded-lg border border-primary p-3">
        <div className="flex justify-between text-sm font-semibold">
          <div>Inputs preview</div>
          <ChevronDownIcon
            className={cn(
              'h-4 w-4 cursor-pointer',
              showInputs ? 'rotate-180' : ''
            )}
            onClick={() => setShowInputs(!showInputs)}
          />
        </div>
        {inputsStr && showInputs && (
          <CodeCard>
            <Code
              language={json()}
              value={inputsStr}
              readOnly={true}
              maxHeight="250px"
            />
          </CodeCard>
        )}
      </div>
      <div className="flex flex-col gap-2 rounded-lg border border-primary p-3">
        <div className="flex justify-between text-sm font-semibold">
          <div>Outputs preview</div>
          <ChevronDownIcon
            className={cn(
              'h-4 w-4 cursor-pointer',
              showOutputs ? 'rotate-180' : ''
            )}
            onClick={() => setShowOutputs(!showOutputs)}
          />
        </div>
        {outputsStr && showOutputs && (
          <CodeCard>
            <Code
              language={json()}
              value={outputsStr}
              readOnly={true}
              maxHeight="250px"
            />
          </CodeCard>
        )}
      </div>
    </div>
  );
}

export function AddToDatasetConfirmation({
  dataset,
  setSelectedDataset,
  selectedRunIds,
  onAddToDataset,
}: {
  dataset: DatasetSchema;
  setSelectedDataset: React.Dispatch<
    React.SetStateAction<DatasetSchema | null>
  >;
  selectedRunIds: string[];
  onAddToDataset: (dataset: DatasetSchema) => void;
}) {
  const [loading, setLoading] = useState(false);
  const { contents } = useRun({
    id: selectedRunIds[0],
    withContents: true,
    excludeSerialized: true,
  });

  const s3Input = contents?.inputs.data;
  const s3Output = contents?.outputs.data;

  const validateExampleParams = useMemo(
    () => ({
      example:
        selectedRunIds && dataset.id
          ? {
              dataset_id: dataset.id,
              inputs: s3Input,
              outputs: s3Output,
              source_run_id: selectedRunIds[0],
            }
          : undefined,
    }),
    [selectedRunIds, dataset, s3Input, s3Output]
  );

  const validatedExample = useValidateExample(validateExampleParams, {
    revalidateOnFocus: false,
    keepPreviousData: false,
  });

  const inputs = validatedExample.data?.inputs ?? s3Input;
  const outputs = validatedExample.data?.outputs ?? s3Output;

  if (!inputs && !outputs) {
    return null;
  }

  return (
    <Modal open={!!dataset} onClose={() => setSelectedDataset(null)}>
      <ModalDialog>
        <ModalClose size="lg" />
        <div className="flex flex-col gap-4">
          <div className="text-lg font-semibold">
            Add {selectedRunIds.length}{' '}
            {pluralize('run', selectedRunIds.length)} to {dataset.name}
          </div>
          <InputsOutputsSection inputs={inputs} outputs={outputs} />
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => setSelectedDataset(null)}
              variant="outlined"
              color="secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                setLoading(true);

                await onAddToDataset(dataset);
                setLoading(false);
                setSelectedDataset(null);
              }}
              loading={loading}
            >
              Add
            </Button>
          </div>
        </div>
      </ModalDialog>
    </Modal>
  );
}

export function AddToDatasetButton({
  // selectedRuns,
  runDisplayText = 'selected run',
  runTypeAndInputs,
  selectedRunIds,
  mutateInfiniteRuns,
  setBulkActionResult,
  isNewDatasetPaneOpen,
  setIsNewDatasetPaneOpen,
}: {
  // selectedRuns?: RunSchema[];
  runDisplayText?: string;
  runTypeAndInputs?: Pick<RunSchema, 'run_type' | 'inputs'>;
  selectedRunIds: string[];
  mutateInfiniteRuns?: () => void;
  setBulkActionResult: (result: BulkActionResult) => void;
  isNewDatasetPaneOpen: boolean;
  setIsNewDatasetPaneOpen: (open: boolean) => void;
}) {
  const workspaceId = useWorkspaceId();

  const [isAddToDatasetPopoverOpen, setIsAddToDatasetPopoverOpen] =
    useState(false);
  const [loading, setLoading] = useState(false);

  const [selectedDataset, setSelectedDataset] = useState<DatasetSchema | null>(
    null
  );

  useEffect(() => {
    if (isNewDatasetPaneOpen) {
      setIsAddToDatasetPopoverOpen(false);
    }
  }, [isNewDatasetPaneOpen]);

  const onAddToDataset = async (dataset: DatasetSchema) => {
    try {
      const res = await handleAddToDataset(
        dataset,
        selectedRunIds,
        workspaceId
      );
      const body = await res.json();
      if (res.ok) {
        mutateInfiniteRuns?.();
        const numDuplicates = selectedRunIds.length - body.length;
        const datasetPath = `/${appOrganizationPath}/${workspaceId}/${appDatasetsPath}/${dataset.id}/`;
        if (numDuplicates > 0 && body.length === 0) {
          setBulkActionResult({
            success: true,
            title: '',
            message: (
              <span>
                <span className="text-md text-white dark:text-black">{`${
                  selectedRunIds.length > 1
                    ? `All the ${runDisplayText}s`
                    : `The ${runDisplayText}`
                } already existed in `}</span>
                <Link fontSize={'sm'} href={datasetPath}>
                  <span className="text-md text-brand-green-200 underline">
                    {dataset.name}
                  </span>
                </Link>
                <span className="text-md text-white dark:text-black">{`, so ${
                  selectedRunIds.length > 1 ? 'they were' : 'it was'
                } not added.`}</span>
              </span>
            ),
          });
        } else if (numDuplicates > 0) {
          setBulkActionResult({
            success: true,
            title: '',
            message: (
              <span>
                <span className="text-md text-white dark:text-black">{`${
                  body.length
                } ${body.length > 1 ? 'runs' : 'run'} added to `}</span>
                <Link fontSize={'sm'} href={datasetPath}>
                  <span className="text-md text-brand-green-200 underline">
                    {dataset.name}
                  </span>
                </Link>
                <span className="text-md text-white dark:text-black">{`. ${numDuplicates} ${
                  numDuplicates > 1
                    ? `duplicates of the ${runDisplayText}s were`
                    : `duplicate of the ${runDisplayText} was`
                } not added.`}</span>
              </span>
            ),
          });
        } else {
          setBulkActionResult({
            success: true,
            title: '',
            message: (
              <span>
                <span className="text-md text-white dark:text-black">{`${
                  body.length
                } ${body.length > 1 ? 'runs' : 'run'} added to `}</span>
                <Link fontSize={'sm'} href={datasetPath}>
                  <span className="text-md text-brand-green-200 underline">
                    {dataset.name}
                  </span>
                </Link>
              </span>
            ),
          });
        }
      } else {
        const typedBody: {
          detail: string | { message: string };
        } = body;

        const msg =
          typeof typedBody.detail === 'object'
            ? typedBody.detail.message
            : typedBody.detail;

        setBulkActionResult({
          success: false,
          title: '',
          message: (
            <span className="text-md text-error">{`Error adding to dataset: ${msg}`}</span>
          ),
        });
      }
    } catch (e) {
      setBulkActionResult({
        success: false,
        title: '',
        message: (
          <span className="text-md text-error">{`Error adding to dataset: ${e?.toString()}`}</span>
        ),
      });
    }
  };

  return (
    <>
      <Popover
        open={isAddToDatasetPopoverOpen}
        onOpenChange={setIsAddToDatasetPopoverOpen}
      >
        <PopoverTrigger asChild>
          <div>
            <Button
              size="md"
              color="primary"
              onClick={() => {
                setIsAddToDatasetPopoverOpen(true);
              }}
              className="grow"
              leftDecorator={CircleStackIcon}
              loading={loading}
            >
              Add to Dataset
            </Button>
          </div>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[508px] p-0"
          avoidCollisions={false}
          side={'top'}
        >
          <DatasetSelectorPopoverContent
            runTypeAndInputs={runTypeAndInputs}
            handleSelectDataset={async (dataset) => {
              setLoading(true);
              setIsAddToDatasetPopoverOpen(false);
              if (dataset.transformations?.length) {
                setSelectedDataset(dataset);
              } else {
                await onAddToDataset(dataset);
              }
              setLoading(false);
            }}
            openNewDatasetPane={() => setIsNewDatasetPaneOpen(true)}
          />
        </PopoverContent>
      </Popover>
      {selectedDataset && (
        <AddToDatasetConfirmation
          dataset={selectedDataset}
          setSelectedDataset={setSelectedDataset}
          selectedRunIds={selectedRunIds}
          onAddToDataset={onAddToDataset}
        />
      )}
    </>
  );
}
